import { isProduction } from '@application/env';
import {
  Button,
  LoadingContainer,
  MtFormButtons,
  MtFormContainer,
  MtLayouTitleEditButton,
  MtLayoutTitleWrapper,
  Section,
  SectionSubtitle,
  SectionTitle,
  ContactPreferences,
  InputDateField,
  InputField,
  InputFileField,
  InputLabel,
  InputRadioField,
  InputWrapper,
  PreferenceList,
  InputSelectField,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import {
  allLanguageSelectOptions,
  buyerPhonePrefixSelectOptions,
  formatContactPreferences,
  genderSelectOptions,
  useRouter,
} from '@application/utils';
import { Form, useFormikContext } from 'formik';
import { CountryCode } from '@gql/graphql';
import { useMemo } from 'react';
import type { FC } from 'react';
import { FaEdit } from 'react-icons/fa';
import type { MtSettingsBuyerPageFormValues, MtSettingsBuyerPageProps } from './types';
import * as Styled from './styled';

export * from './types';

export const MtSettingsBuyerPage: FC<MtSettingsBuyerPageProps> = (props) => {
  const {
    mode,
    onEditClick,
    onCancelClick,
    onDeleteAccountClick,
    onChangePasswordClick,
    loading = false,
    socialProvidersComponent,
    showChangePassword = false,
    onChangeContactPreferenceClick,
  } = props;
  const { translate } = useTranslation();

  const { countryCode, language } = useRouter();
  const { resetForm, isSubmitting } = useFormikContext<MtSettingsBuyerPageFormValues>();

  const emailPreferences = formatContactPreferences(translate);

  const allowLanguageChange = useMemo(() => {
    return [CountryCode.CH, CountryCode.BE].includes(countryCode);
  }, [countryCode]);

  const phonePrefixes = buyerPhonePrefixSelectOptions(translate);

  if (loading) return <LoadingContainer />;

  return (
    <MtFormContainer>
      <MtLayoutTitleWrapper>
        <SectionTitle>{translate('mt.buyer.settings.title')}</SectionTitle>
        {mode === 'view' && (
          <MtLayouTitleEditButton onClick={onEditClick}>
            <span>{translate('common.buttons.edit')}</span>
            <FaEdit />
          </MtLayouTitleEditButton>
        )}
      </MtLayoutTitleWrapper>
      <Form>
        <Section>
          <SectionSubtitle>{translate('mt.preferences.general.title')}</SectionSubtitle>

          <InputWrapper split>
            <InputLabel>{translate('common.inputs.profilePicture.label')}</InputLabel>
            <Styled.ProfileImageWrapper>
              <InputFileField name="image" disabled={mode === 'view'} />
            </Styled.ProfileImageWrapper>
          </InputWrapper>

          <InputWrapper split last>
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.firstName.label')}</InputLabel>
              <InputField disabled={mode === 'view'} name="firstName" />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{translate('common.inputs.lastName.label')}</InputLabel>
              <InputField disabled={mode === 'view'} name="lastName" />
            </InputWrapper>
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.email.label')}</InputLabel>
              <InputField name="email" type="email" disabled />
            </InputWrapper>
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.phone.label')}</InputLabel>
              <InputWrapper inline width="39%" fullWidthBreakpoint="min" noMargin>
                <InputSelectField
                  name="phonePrefixCountry"
                  options={phonePrefixes}
                  disabled={mode === 'view'}
                />
              </InputWrapper>
              <InputWrapper inline width="61%" fullWidthBreakpoint="min" last noMargin>
                <InputField disabled={mode === 'view'} name="phone" type="tel" />
              </InputWrapper>
            </InputWrapper>
            <InputWrapper>
              <InputLabel>{translate('common.inputs.birthday.label')}</InputLabel>
              <InputDateField
                disabled={mode === 'view'}
                name="birthday"
                calendarProps={{ defaultView: 'decade', maxDate: new Date() }}
              />
            </InputWrapper>
          </InputWrapper>

          {allowLanguageChange ? (
            <PreferenceList.Item>
              <PreferenceList.PLabel>
                {translate('common.inputs.language.label')}
              </PreferenceList.PLabel>

              <InputRadioField
                disabled={mode === 'view'}
                grow
                name="language"
                options={allLanguageSelectOptions(language, ['fr', 'nl'])}
              />
            </PreferenceList.Item>
          ) : null}

          <PreferenceList.Item>
            <PreferenceList.PLabel>{translate('common.inputs.gender.label')}</PreferenceList.PLabel>

            <InputRadioField
              disabled={mode === 'view'}
              grow
              name="gender"
              options={genderSelectOptions(translate, {
                isHuman: true,
                showOther: false,
                showIcons: false,
                showText: true,
              })}
            />
          </PreferenceList.Item>
          <PreferenceList.Item>
            <PreferenceList.PLabel>
              {translate('common.inputs.deleteAccount.label')}
            </PreferenceList.PLabel>
            <Button
              color="red"
              size="small"
              shrink
              disabled={mode !== 'view'}
              onClick={onDeleteAccountClick}
            >
              {translate('common.buttons.delete')}
            </Button>
          </PreferenceList.Item>

          {showChangePassword && (
            <PreferenceList.Item>
              <PreferenceList.PLabel>
                {translate('common.inputs.changePassword.label')}
              </PreferenceList.PLabel>

              <Button
                color="blue"
                size="small"
                shrink
                disabled={mode !== 'view'}
                onClick={onChangePasswordClick}
              >
                {translate('common.buttons.change')}
              </Button>
            </PreferenceList.Item>
          )}
        </Section>

        {socialProvidersComponent}

        {!isProduction() && (
          <Section>
            <ContactPreferences
              mode={mode}
              onChangeContactPreferenceClick={onChangeContactPreferenceClick}
              emails={emailPreferences}
            />
          </Section>
        )}

        <Section>
          <MtFormButtons
            showCancel={mode === 'edit'}
            showSubmit={mode === 'edit'}
            submitLoading={isSubmitting}
            onCancelClick={(e) => {
              resetForm();
              onCancelClick?.(e);
            }}
          />
        </Section>
      </Form>
    </MtFormContainer>
  );
};
